import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Wallet, Upload, X, Copy, ArrowRight, ArrowLeft, CheckCircle2, Loader2, CreditCard, IndianRupee, Receipt, ScanLine } from 'lucide-react';
import { toast } from 'sonner';
import { getUserWallet, userUploadScreenshot, userDeposit } from '../../lib/api';
import { resolveMediaUrl } from '../admin/MediaUpload';

/**
 * Multi-step deposit modal:
 *   1. Enter amount + method picker
 *   2. Payment details (UPI / bank / QR) + Copy buttons
 *   3. Upload screenshot + UTR/Txn ID
 *   4. Submitted confirmation
 */
export default function DepositModal({ open, onOpenChange, onSubmitted }) {
  const [step, setStep] = useState(1);
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('upi');
  const [utr, setUtr] = useState('');
  const [ssUrl, setSsUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setStep(1); setAmount(''); setMethod('upi'); setUtr(''); setSsUrl('');
    getUserWallet().then(setData).catch(() => toast.error('Failed to load payment methods'));
  }, [open]);

  const pm = data?.payment_methods || {};
  const hasUPI = !!pm.upi_id;
  const hasBank = !!(pm.bank_name || pm.account_number);
  const hasQR = !!pm.qr_image;
  const availableMethods = [
    hasUPI && { key: 'upi', label: 'UPI', icon: IndianRupee, desc: pm.upi_id },
    hasBank && { key: 'bank', label: 'Bank Transfer', icon: CreditCard, desc: pm.bank_name },
    hasQR && { key: 'qr', label: 'Scan QR', icon: ScanLine, desc: 'Scan with any UPI app' }
  ].filter(Boolean);

  const copy = (t, l) => { navigator.clipboard?.writeText(t); toast.success(`${l} copied`); };

  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    setUploading(true);
    try { const res = await userUploadScreenshot(f); setSsUrl(res.url); toast.success('Screenshot uploaded'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const next1 = () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error('Enter a valid amount');
    if (availableMethods.length === 0) return toast.error('Admin has not configured payment methods yet');
    setStep(2);
  };
  const submit = async () => {
    if (!ssUrl) return toast.error('Upload payment screenshot');
    setSubmitting(true);
    try {
      const sel = availableMethods.find((m) => m.key === method);
      await userDeposit({
        amount: parseFloat(amount),
        screenshot_url: ssUrl,
        method: sel?.label || '',
        note: utr ? `UTR/Txn: ${utr}` : ''
      });
      setStep(4);
      onSubmitted?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] w-full sm:max-h-[92vh] overflow-y-auto bg-gradient-to-b from-[#1a1a1a] to-[#0c0c0c] border border-zinc-800 text-zinc-100 p-0 sm:rounded-2xl [&>button]:hidden" data-testid="deposit-modal">
        <DialogTitle className="sr-only">Add Money to Wallet</DialogTitle>
        <DialogDescription className="sr-only">Deposit money into your wallet using UPI, bank transfer or QR.</DialogDescription>
        <span className="absolute right-3 top-3 z-20">
          <button onClick={() => onOpenChange(false)} className="w-9 h-9 rounded-full text-zinc-300 hover:text-white bg-black/40 hover:bg-red-500/80 border border-white/10 flex items-center justify-center" data-testid="deposit-close-btn"><X size={18} /></button>
        </span>

        {/* Header */}
        <div className="px-5 sm:px-6 pt-6 pb-4 bg-gradient-to-r from-[color:var(--gold)]/15 to-transparent">
          <div className="flex items-center gap-2 mb-1 text-[color:var(--gold)]"><Wallet size={20} /> <span className="font-bold uppercase tracking-wider text-sm">Add Money</span></div>
          <h2 className="text-2xl sm:text-3xl font-extrabold">Deposit to Wallet</h2>
          <div className="mt-3 flex items-center gap-1.5">
            {[1, 2, 3].map((n) => (
              <div key={n} className={`h-1.5 rounded-full flex-1 ${step >= n ? 'bg-[color:var(--gold)]' : 'bg-zinc-800'}`} />
            ))}
          </div>
        </div>

        <div className="p-5 sm:p-6">
          {/* Step 1 — amount + method */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="text-zinc-300 text-xs font-bold uppercase tracking-wider">Enter Amount</label>
                <div className="relative mt-2">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 text-2xl">₹</span>
                  <Input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))} type="text" inputMode="decimal" placeholder="0" className="h-16 pl-10 text-3xl font-bold bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-700" data-testid="deposit-amount-input" autoFocus />
                </div>
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  {[100, 500, 1000, 2000, 5000].map((q) => (
                    <button key={q} onClick={() => setAmount(String(q))} className="text-xs px-2.5 py-1 rounded-md bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300" data-testid={`deposit-quick-${q}`}>+₹{q}</button>
                  ))}
                </div>
              </div>
              {availableMethods.length > 0 && (
                <div>
                  <label className="text-zinc-300 text-xs font-bold uppercase tracking-wider">Pay Using</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                    {availableMethods.map((m) => {
                      const Icon = m.icon;
                      const on = method === m.key;
                      return (
                        <button key={m.key} onClick={() => setMethod(m.key)} className={`text-left p-3 rounded-xl border-2 transition ${on ? 'bg-[color:var(--gold)]/10 border-[color:var(--gold)]' : 'bg-zinc-950 border-zinc-800 hover:border-zinc-700'}`} data-testid={`deposit-method-${m.key}`}>
                          <div className="flex items-center gap-2 mb-0.5">
                            <Icon size={16} className={on ? 'text-[color:var(--gold)]' : 'text-zinc-400'} />
                            <span className="font-bold">{m.label}</span>
                            {on && <CheckCircle2 size={14} className="text-[color:var(--gold)] ml-auto" />}
                          </div>
                          <div className="text-[11px] text-zinc-400 truncate">{m.desc}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {availableMethods.length === 0 && data && (
                <div className="text-amber-400 text-sm p-3 rounded-md bg-amber-500/10 border border-amber-500/30">
                  No payment methods configured yet. Please contact admin.
                </div>
              )}
              <Button onClick={next1} className="w-full h-12 bg-[color:var(--gold)] hover:brightness-110 text-black font-bold text-base" data-testid="deposit-step1-next">
                Continue <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          )}

          {/* Step 2 — payment details */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-zinc-400 text-xs uppercase tracking-wider">Amount</div>
                <div className="text-4xl font-extrabold text-[color:var(--gold)]" data-testid="deposit-amount-display">₹{amount}</div>
              </div>
              <div className="rounded-2xl border border-zinc-800 bg-zinc-950 p-4 space-y-3" data-testid="deposit-details">
                {method === 'upi' && hasUPI && (
                  <div className="space-y-2">
                    <div className="text-zinc-300 text-xs font-bold">Pay to UPI ID</div>
                    <div className="flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2.5">
                      <span className="font-mono text-lg text-white truncate" data-testid="deposit-upi-id">{pm.upi_id}</span>
                      <button onClick={() => copy(pm.upi_id, 'UPI ID')} className="text-[color:var(--gold)] flex items-center gap-1 text-sm font-bold shrink-0 ml-2"><Copy size={14} /> Copy</button>
                    </div>
                  </div>
                )}
                {method === 'bank' && hasBank && (
                  <div className="space-y-2">
                    {pm.bank_name && <div className="flex justify-between"><span className="text-zinc-400 text-sm">Bank</span><span className="font-bold">{pm.bank_name}</span></div>}
                    {pm.account_holder && <div className="flex justify-between"><span className="text-zinc-400 text-sm">Holder</span><span className="font-bold">{pm.account_holder}</span></div>}
                    {pm.account_number && (
                      <div className="flex justify-between items-center"><span className="text-zinc-400 text-sm">A/C #</span>
                        <span className="font-mono font-bold flex items-center gap-2">{pm.account_number} <button onClick={() => copy(pm.account_number, 'A/C')} className="text-[color:var(--gold)]"><Copy size={12} /></button></span>
                      </div>
                    )}
                    {pm.ifsc && (
                      <div className="flex justify-between items-center"><span className="text-zinc-400 text-sm">IFSC</span>
                        <span className="font-mono font-bold flex items-center gap-2">{pm.ifsc} <button onClick={() => copy(pm.ifsc, 'IFSC')} className="text-[color:var(--gold)]"><Copy size={12} /></button></span>
                      </div>
                    )}
                  </div>
                )}
                {method === 'qr' && hasQR && (
                  <div className="flex flex-col items-center">
                    <div className="text-zinc-300 text-xs font-bold mb-2">Scan with any UPI app</div>
                    <img src={resolveMediaUrl(pm.qr_image)} alt="QR" className="w-56 h-56 rounded-lg border-4 border-white bg-white object-contain" data-testid="deposit-qr-img" />
                  </div>
                )}
                {pm.notes && <div className="text-xs text-zinc-400 italic pt-2 border-t border-zinc-800">{pm.notes}</div>}
              </div>
              <div className="text-xs text-zinc-500 text-center px-3">After paying, click Next to upload your payment screenshot.</div>
              <div className="flex gap-2">
                <Button onClick={() => setStep(1)} variant="ghost" className="text-zinc-300 hover:bg-zinc-900"><ArrowLeft size={14} className="mr-1" /> Back</Button>
                <Button onClick={() => setStep(3)} className="flex-1 h-12 bg-[color:var(--gold)] hover:brightness-110 text-black font-bold" data-testid="deposit-step2-next">
                  I&apos;ve Paid <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3 — upload + UTR */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <label className="text-zinc-300 text-xs font-bold uppercase tracking-wider">UTR / Transaction ID (Optional)</label>
                <Input value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="e.g. 458291734512" className="mt-2 h-11 bg-zinc-950 border-zinc-800 text-zinc-100" data-testid="deposit-utr-input" />
              </div>
              <div>
                <label className="text-zinc-300 text-xs font-bold uppercase tracking-wider">Payment Screenshot *</label>
                <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="deposit-screenshot-input" />
                <div className="mt-2">
                  {ssUrl ? (
                    <div className="relative inline-block">
                      <img src={resolveMediaUrl(ssUrl)} alt="" className="max-h-64 rounded-lg border border-zinc-800" />
                      <button onClick={() => setSsUrl('')} className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center"><X size={14} /></button>
                    </div>
                  ) : (
                    <button onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full h-32 rounded-xl border-2 border-dashed border-zinc-700 hover:border-[color:var(--gold)] flex flex-col items-center justify-center gap-2 text-zinc-400 hover:text-zinc-200 transition disabled:opacity-60" data-testid="deposit-upload-zone">
                      {uploading ? <><Loader2 size={24} className="animate-spin" /><span>Uploading…</span></> : <><Upload size={24} /><span className="font-bold">Tap to upload screenshot</span><span className="text-xs text-zinc-600">PNG / JPG · max 8 MB</span></>}
                    </button>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setStep(2)} variant="ghost" className="text-zinc-300 hover:bg-zinc-900"><ArrowLeft size={14} className="mr-1" /> Back</Button>
                <Button onClick={submit} disabled={submitting || !ssUrl} className="flex-1 h-12 bg-[color:var(--gold)] hover:brightness-110 text-black font-bold disabled:opacity-60" data-testid="deposit-submit-btn">
                  {submitting ? 'Submitting…' : <>Submit Request <Receipt size={16} className="ml-2" /></>}
                </Button>
              </div>
            </div>
          )}

          {/* Step 4 — confirmation */}
          {step === 4 && (
            <div className="py-6 text-center space-y-3" data-testid="deposit-success">
              <div className="w-20 h-20 rounded-full bg-emerald-500/20 border-4 border-emerald-500/40 mx-auto flex items-center justify-center">
                <CheckCircle2 size={40} className="text-emerald-400" />
              </div>
              <h3 className="text-2xl font-bold">Request Submitted!</h3>
              <p className="text-zinc-400 max-w-xs mx-auto">Your deposit of <span className="text-[color:var(--gold)] font-bold">₹{amount}</span> is pending admin approval. You&apos;ll see your wallet update once approved.</p>
              <Button onClick={() => onOpenChange(false)} className="bg-[color:var(--gold)] hover:brightness-110 text-black font-bold px-8">Done</Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
